import React, { useState } from 'react';
import { logTrailDetailsAddToListButtonTapped } from '@alltrails/analytics/amplitudeFunctions';
import BookmarkButton from '@alltrails/shared/components/BookmarkButton';
import { logTrailDetailsPhotoClicked, TrailDetailsPhotoLocations } from 'utils/trail_analytics_helpers';
import { wrapEventHandler } from 'utils/handlers';
import Trail from 'types/Trails/Trail';
import { urlReformatPhotos } from 'utils/lightbox_helpers';
import { Context } from '@alltrails/shared/types/context';
import CompletedBadge from 'components/shared/badges/CompletedBadge';
import CustomProvider from 'components/CustomProvider';
import Difficulty from 'components/shared/Difficulty';
import ReviewCount from 'components/shared/ReviewCount';
import StarRating from 'components/shared/StarRating';
import { getTrailProfilePhotoUrl } from 'utils/trail_helpers';
import Lightbox from 'components/shared/Lightbox';
import { defineMessages, useIntl } from 'react-intl';
import CardLocation from '../CardLocation';
import * as styles from './TrailDetailsCard.module.scss';
import * as cardStyles from '../CardContent/styles/styles.module.scss';

const messages = defineMessages({
  altTitle: { defaultMessage: '{list} | AllTrails.com' }
});

type Props = {
  exploreFlag?: boolean;
  isPending?: boolean;
  numPhotos?: number;
  photoUrl?: string;
  context?: Context;
  trail?: Trail;
  photos?: any;
  listMethods: any;
  photoId: number;
};

export default function TrailDetailsCard({ isPending, photoUrl, trail, photos = [], listMethods, photoId }: Props) {
  const intl = useIntl();
  const [lightboxOpen, setLightboxOpen] = useState(false);

  function handleCloseRequest() {
    setLightboxOpen(false);
  }

  function getLightboxProps() {
    return {
      images: urlReformatPhotos(photos),
      handleCloseRequest,
      isOpen: true,
      startingIndex: 0,
      trailId: trail.trail_id
    };
  }

  function openLightbox() {
    setLightboxOpen(true);
  }

  function renderLightbox() {
    if (!lightboxOpen) {
      return null;
    }

    return <Lightbox {...getLightboxProps()} />;
  }

  function onClick() {
    if (!lightboxOpen) {
      logTrailDetailsPhotoClicked(trail.trail_id, photoId, TrailDetailsPhotoLocations.HeroImage);
    }
    openLightbox();
  }

  const trailPhotoUrl = photoUrl || getTrailProfilePhotoUrl(trail);
  let displayName = trail.name;

  if (isPending) {
    displayName += ' [PENDING]';
  }

  const handleFavoriteClick = () => {
    logTrailDetailsAddToListButtonTapped({ trail_id: trail.trail_id });
    listMethods.handleFavoriteClick(trail.type, trail.ID, trail.objectID);
  };

  const altTitle = intl.formatMessage(messages.altTitle, {
    // these fields are already translated from the server
    list: [trail.name, trail.area_name, trail.city_name, trail.state_name, trail.country_name].filter(Boolean).join(', ')
  });

  return (
    <CustomProvider>
      <div
        className={['xlate-none', styles.card, photos.length > 0 ? styles.clickable : ''].join(' ')}
        itemProp="image"
        title={trail.name}
        {...(photos.length > 0 ? { onClick, role: 'button' } : {})}
      >
        {trailPhotoUrl && <img src={trailPhotoUrl} alt={altTitle} className={styles.backgroundImage} title={altTitle} />}
        <div className={styles.content}>
          <h1 className={['xlate-none', styles.name].join(' ')} itemProp="name" title={trail.name}>
            {displayName}
          </h1>
          <div className={`${cardStyles.info} ${cardStyles.thin}`}>
            <Difficulty difficulty={trail.difficulty_rating} />
            <span className={styles.starRatingWrapper} itemProp="aggregateRating" itemScope itemType="http://schema.org/AggregateRating">
              <StarRating config={trail.avg_rating} />
              <meta itemProp="ratingValue" content={trail.avg_rating == null ? '0' : String(trail.avg_rating)} />
              <meta itemProp="worstRating" content="0" />
              <meta itemProp="bestRating" content="5" />
              <meta itemProp="reviewCount" content={String(trail.num_reviews)} />
            </span>
            <ReviewCount className={styles.reviewCount} count={trail.num_reviews} />
          </div>
          <CardLocation className={styles.location} trail={trail} />
        </div>
        <CompletedBadge
          className={styles.badge}
          isCompleted={listMethods.isComplete(trail.type, trail.ID)}
          isVerified={listMethods.isVerified(trail.type, trail.ID)}
        />
        <BookmarkButton
          className={styles.favorite}
          onClick={wrapEventHandler(handleFavoriteClick)}
          isSelected={listMethods.isFavorite(trail.type, trail.ID)}
        />
        {renderLightbox()}
      </div>
    </CustomProvider>
  );
}
